import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, List, X, LinkedinLogo } from "@phosphor-icons/react";
import { C, R } from "@/brand/tokens";
import { brand6, nav6 } from "@/brand/contentF6";
import { LogoWordmark } from "@/components/brand/LogoWordmark";
import { Cta6 } from "@/components/common/Cta6";

const slug = (label) => label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "");

/**
 * SiteHeader6 — Faith 1st Consulting primary header.
 * Stacked wordmark (no icon mark) · Services / About / Speaking & Keynotes /
 * Resources / Process / Contact · persistent "Book a consultation" CTA.
 */
export function SiteHeader6() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <header
      data-testid="site-header-6"
      className="sticky top-0 z-40 backdrop-blur"
      style={{ background: `${C.surfaceLight}F2`, borderBottom: `1px solid ${C.brassSoft}` }}
    >
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10 h-24 flex items-center justify-between gap-5">
        <Link
          to="/"
          data-testid="brand-mark-6"
          className="inline-flex items-center shrink-0"
          aria-label={`${brand6.wordmark} — home`}
          onClick={() => setDrawerOpen(false)}
        >
          <LogoWordmark tone="ink" height={56} className="sm:hidden" />
          <LogoWordmark tone="ink" height={76} className="hidden sm:block" />
        </Link>

        <nav className="hidden xl:flex items-center gap-7" data-testid="nav-links-6" aria-label="Primary">
          {nav6.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              data-testid={`nav-${slug(link.label)}`}
              className="font-inter text-[10.5px] uppercase font-semibold whitespace-nowrap transition-colors"
              style={{ color: C.textOnLight, letterSpacing: "0.16em" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = C.surfacePrimary)}
              onMouseLeave={(e) => (e.currentTarget.style.color = C.textOnLight)}
            >
              {link.label}
            </Link>
          ))}
          <a
            href={brand6.linkedin}
            target="_blank"
            rel="noreferrer"
            data-testid="header-linkedin"
            aria-label="Katrina Bernard on LinkedIn"
            className="inline-flex items-center transition-colors"
            style={{ color: C.textOnLight }}
            onMouseEnter={(e) => (e.currentTarget.style.color = C.surfacePrimary)}
            onMouseLeave={(e) => (e.currentTarget.style.color = C.textOnLight)}
          >
            <LinkedinLogo size={17} weight="regular" />
          </a>
        </nav>

        <div className="hidden xl:block shrink-0">
          <Cta6 to="/contact" testId="header-cta-6" variant="primary" className="px-5 py-3">
            {brand6.ctaLabel}
          </Cta6>
        </div>

        {/* Mobile / tablet — compact CTA + hamburger */}
        <div className="xl:hidden flex items-center gap-2">
          <Link
            to="/contact"
            data-testid="header-cta-6-mobile"
            className="inline-flex items-center gap-2 px-4 py-2.5 font-inter text-[10px] uppercase font-semibold whitespace-nowrap"
            style={{ background: C.surfacePrimary, color: C.textOnDark, letterSpacing: "0.2em", borderRadius: R.button }}
          >
            Book
            <ArrowRight size={10} weight="bold" />
          </Link>
          <button
            type="button"
            data-testid="header-menu-toggle"
            aria-label={drawerOpen ? "Close menu" : "Open menu"}
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((v) => !v)}
            className="inline-flex items-center justify-center w-11 h-11"
            style={{ border: `1px solid ${C.brassLine}`, color: C.textOnLight, background: "transparent", borderRadius: R.button }}
          >
            {drawerOpen ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
          </button>
        </div>
      </div>

      {drawerOpen && (
        <div
          data-testid="header-mobile-drawer"
          className="xl:hidden"
          style={{ background: C.surfaceLight2, borderTop: `1px solid ${C.brassSoft}` }}
        >
          <nav className="mx-auto max-w-[1320px] px-5 sm:px-8 py-6 flex flex-col" aria-label="Mobile primary">
            {nav6.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                data-testid={`nav-mobile-${slug(link.label)}`}
                className="py-4 font-inter text-[13px] uppercase font-semibold flex items-center justify-between"
                style={{ color: C.textOnLight, letterSpacing: "0.22em", borderTop: `1px solid ${C.brassSoft}` }}
                onClick={() => setDrawerOpen(false)}
              >
                {link.label}
                <ArrowRight size={12} weight="bold" style={{ color: C.accentMetal }} />
              </Link>
            ))}
            <a
              href={brand6.linkedin}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-mobile-linkedin"
              className="py-4 font-inter text-[13px] uppercase font-semibold flex items-center justify-between"
              style={{ color: C.textOnLight, letterSpacing: "0.22em", borderTop: `1px solid ${C.brassSoft}` }}
            >
              LinkedIn
              <LinkedinLogo size={14} weight="regular" style={{ color: C.accentMetal }} />
            </a>
            <div className="mt-5">
              <Cta6 to="/contact" testId="drawer-cta-6" className="w-full justify-between" onClick={() => setDrawerOpen(false)}>
                {brand6.ctaLabel}
              </Cta6>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
